/**
 * PublicHoursBanner · v2.8+E302
 * ─────────────────────────────
 * Public-facing business-hours display, driven by the SAME live Hours-of-
 * Operation config as the rider portal (GET /api/rider-portal/hours) so it
 * always matches and auto-updates when dispatch edits hours in admin.
 *
 *   variant="strip" → slim homepage banner (sits ABOVE the DON'T RISK IT
 *                     CTA banner; intentionally much smaller so it doesn't
 *                     compete). Amber-on-zinc to match the home color scheme.
 *   variant="card"  → prominent labeled section for the Contact page.
 *
 * Read-only display. Changes no booking logic.
 */
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import usePortalHours from "@/hooks/usePortalHours";
import ReopenCountdown from "@/components/ReopenCountdown";

export default function PublicHoursBanner({ variant = "strip" }) {
  const h = usePortalHours();
  if (!h) return null;

  // v2.8+E310 · Public "business hours" DISPLAY (phone/dispatch = 8:00 AM–3:00 AM),
  // decoupled from the online-booking gate (is_open / next_open_at = 10:00 AM).
  const time = (h.business_hours_label || "8:00 AM – 3:00 AM").replace(/\s*(CDT|CST)\b/i, "").trim();
  const days = h.days_label || "7 days a week";
  const tz = h.tz_abbrev || "CDT";
  const closed = h.is_open === false;
  const nextOpenAt = h.next_open_at;

  if (variant === "login") {
    return (
      <div className="mt-4 rounded-xl bg-zinc-900/80 border border-amber-400/30 px-4 py-3 flex items-center gap-3 backdrop-blur" data-testid="rider-login-hours">
        <div className="h-9 w-9 rounded-lg bg-amber-400/15 border border-amber-400/40 flex items-center justify-center text-amber-300 shrink-0">
          <Clock className="h-5 w-5" />
        </div>
        <div className="text-left">
          <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-black">Hours of Operation</div>
          <div className="text-sm font-bold text-zinc-100" data-testid="rider-login-hours-value">{days}, {time} <span className="text-zinc-500 font-normal">{tz}</span></div>
          {closed && nextOpenAt && (
            <ReopenCountdown
              nextOpenAt={nextOpenAt}
              testId="rider-login-reopen-countdown"
              prefix="Reopens in "
              className="text-[11px] font-bold text-emerald-300"
            />
          )}
        </div>
      </div>
    );
  }

  if (variant === "card") {
    return (
      <section className="max-w-6xl mx-auto px-5 pt-8" data-testid="contact-hours">
        <div className="rounded-2xl bg-amber-400/10 border border-amber-400/40 px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-amber-400/20 border border-amber-400/40 flex items-center justify-center text-amber-300">
              <Clock className="h-6 w-6" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-300 font-black">Hours of Operation</div>
          </div>
          <div className="sm:ml-auto">
            <div className="text-lg sm:text-2xl font-extrabold text-zinc-100" data-testid="contact-hours-value">
              {days}, {time}
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">Central Time ({tz}) · online booking window</div>
            {closed && nextOpenAt && (
              <ReopenCountdown
                nextOpenAt={nextOpenAt}
                testId="contact-reopen-countdown"
                prefix="Reopens in "
                className="mt-1 inline-block text-xs sm:text-sm font-bold text-emerald-300"
              />
            )}
          </div>
        </div>
      </section>
    );
  }

  // Homepage strip — noticeably smaller than the CTA banner below it.
  return (
    <div className="max-w-7xl mx-auto px-5 pt-3" data-testid="home-hours-banner">
      <div className="rounded-full bg-zinc-900/80 border border-amber-400/30 px-4 py-1.5 flex items-center justify-center gap-2 text-center flex-wrap">
        <Clock className="h-3.5 w-3.5 text-amber-300 shrink-0" />
        <span className="text-[11px] sm:text-xs font-bold text-zinc-200" data-testid="home-hours-value">
          <span className="text-amber-300 uppercase tracking-wider">Hours:</span> {days} · {time}{" "}
          <span className="text-zinc-500">{tz}</span>
        </span>
        {closed && nextOpenAt && (
          <span className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-bold text-emerald-300" data-testid="home-hours-reopen">
            <span className="text-zinc-600">·</span>
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"/>
            <ReopenCountdown nextOpenAt={nextOpenAt} testId="home-reopen-countdown" prefix="reopens in "/>
          </span>
        )}
        {!closed && (
          <Link
            to="/ride/login"
            data-testid="home-hours-open-badge"
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 px-2.5 py-0.5 text-[11px] sm:text-xs font-black text-emerald-300 hover:bg-emerald-500/25 hover:text-emerald-200 transition-colors"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"/>
            Open now — book instantly
          </Link>
        )}
      </div>
    </div>
  );
}
